import React, { useEffect, useState, useCallback } from 'react';
import {
  adminGetUsers, adminDeleteUser, adminBlockUser,
  adminUpdateUser, adminResetPassword,
} from '../../api/api';
import {
  Search, UserX, UserCheck, Trash2, Edit2, Key,
  ChevronLeft, ChevronRight, Shield, User,
} from 'lucide-react';
import StyledSelect from '../../components/StyledSelect';

const PRIMARY = '#c44569';

/* ── tiny badge ── */
const Badge = ({ label, color, bg }) => (
  <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ background: bg, color }}>{label}</span>
);

/* ── confirm modal ── */
const Confirm = ({ message, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
    style={{ background: 'rgba(30,10,18,0.5)', backdropFilter: 'blur(3px)' }}>
    <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
      <p className="text-slate-800 font-semibold mb-5">{message}</p>
      <div className="flex gap-3 justify-end">
        <button onClick={onCancel}
          className="px-4 py-2 rounded-xl text-sm font-semibold transition"
          style={{ background: '#f1f5f9', color: '#64748b' }}
          onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
          onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}>Cancel</button>
        <button onClick={onConfirm}
          className="px-4 py-2 rounded-xl text-sm font-semibold text-white transition"
          style={{ background: '#ef4444' }}
          onMouseEnter={e => e.currentTarget.style.background = '#dc2626'}
          onMouseLeave={e => e.currentTarget.style.background = '#ef4444'}>Confirm</button>
      </div>
    </div>
  </div>
);

/* ── edit modal ── */
const EditModal = ({ user, onSave, onClose }) => {
  const roleOptions = [
    { value: 'user', label: 'User' },
    { value: 'admin', label: 'Admin' },
  ];
  const emailStatusOptions = [
    { value: '1', label: 'Verified' },
    { value: '0', label: 'Unverified' },
  ];

  const [form, setForm] = useState({
    first_name:     user.first_name || '',
    last_name:      user.last_name  || '',
    email:          user.email      || '',
    phone:          user.phone      || '',
    role:           user.role       || 'user',
    email_verified: user.email_verified ? '1' : '0',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setSaving(true);
    setErr('');
    try {
      const payload = { ...form, email_verified: parseInt(form.email_verified) };
      await adminUpdateUser(user.id, payload);
      onSave();
    } catch (e) {
      setErr(e.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const field = (label, name, type = 'text') => (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      <input
        className="w-full px-3 py-2 rounded-xl border text-slate-900 text-sm outline-none transition"
        style={{ borderColor: '#e2e8f0' }}
        type={type} value={form[name]}
        onChange={e => setForm(f => ({ ...f, [name]: e.target.value }))}
        onFocus={e => e.currentTarget.style.borderColor = PRIMARY}
        onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: 'rgba(30,10,18,0.5)', backdropFilter: 'blur(3px)' }}>
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full space-y-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-base font-bold text-slate-900">Edit User</h3>
        {err && <p className="text-xs text-red-500 font-medium">{err}</p>}
        <div className="grid grid-cols-2 gap-3">
          {field('First Name', 'first_name')}
          {field('Last Name',  'last_name')}
        </div>
        {field('Email', 'email', 'email')}
        {field('Phone', 'phone', 'tel')}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Role</label>
            <StyledSelect
              value={form.role}
              onChange={(next) => setForm((f) => ({ ...f, role: next }))}
              options={roleOptions}
              placeholder="Select role"
              searchable
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Email Verified</label>
            <StyledSelect
              value={form.email_verified}
              onChange={(next) => setForm((f) => ({ ...f, email_verified: next }))}
              options={emailStatusOptions}
              placeholder="Email status"
            />
          </div>
        </div>
        <div className="flex gap-3 justify-end pt-1">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold transition"
            style={{ background: '#f1f5f9', color: '#64748b' }}
            onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
            onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}>Cancel</button>
          <button onClick={submit} disabled={saving}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-60 transition"
            style={{ background: PRIMARY }}
            onMouseEnter={e => e.currentTarget.style.background = '#b8366d'}
            onMouseLeave={e => e.currentTarget.style.background = PRIMARY}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

/* ── reset password modal ── */
const ResetPwModal = ({ user, onClose }) => {
  const [pw, setPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const submit = async () => {
    if (pw.length < 6) { setMsg('Min 6 characters'); return; }
    setSaving(true);
    try {
      await adminResetPassword(user.id, { password: pw });
      setMsg('Password reset successfully!');
      setTimeout(onClose, 1200);
    } catch (e) {
      setMsg(e.response?.data?.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: 'rgba(30,10,18,0.5)', backdropFilter: 'blur(3px)' }}>
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full space-y-4">
        <h3 className="text-base font-bold text-slate-900">Reset Password for {user.name}</h3>
        {msg && <p className="text-xs font-medium" style={{ color: msg.includes('success') ? '#10b981' : '#ef4444' }}>{msg}</p>}
        <input className="w-full px-3 py-2 rounded-xl border text-sm outline-none transition" style={{ borderColor: '#e2e8f0' }}
          placeholder="New password (min 6 chars)" type="password" value={pw}
          onChange={e => setPw(e.target.value)}
          onFocus={e => e.currentTarget.style.borderColor = PRIMARY}
          onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'} />
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold transition"
            style={{ background: '#f1f5f9', color: '#64748b' }}
            onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
            onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}>Cancel</button>
          <button onClick={submit} disabled={saving}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-60 transition"
            style={{ background: PRIMARY }}
            onMouseEnter={e => e.currentTarget.style.background = '#b8366d'}
            onMouseLeave={e => e.currentTarget.style.background = PRIMARY}>{saving ? 'Saving…' : 'Reset'}</button>
        </div>
      </div>
    </div>
  );
};

/* ──────────────── Main Component ──────────────── */
export default function AdminUsers() {
  const [data, setData]         = useState({ users: [], total: 0, totalPages: 1 });
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [confirm, setConfirm]   = useState(null);   // { type, user }
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminGetUsers({ page, limit: 15, search })
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  /* ── Actions ── */
  const doDelete = async (user) => {
    await adminDeleteUser(user.id);
    setConfirm(null);
    load();
  };

  const doBlock = async (user, blocked) => {
    await adminBlockUser(user.id, { is_blocked: blocked });
    setConfirm(null);
    load();
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900">Users</h1>
          <p className="text-slate-500 text-sm mt-1">{data.total} total users</p>
        </div>
        {/* Search */}
        <div className="relative w-full sm:w-64">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="w-full pl-9 pr-3 py-2 rounded-xl border text-sm outline-none transition"
            style={{ borderColor: '#e2e8f0' }}
            placeholder="Search name / email…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            onFocus={e => e.currentTarget.style.borderColor = PRIMARY}
            onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-7 h-7 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: PRIMARY, borderTopColor: 'transparent' }} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['#','Name','Email','Phone','Role','Verified','Blocked','Joined','Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.users.map(u => (
                  <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-slate-400 text-xs">{u.id}</td>
                    <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">{u.name}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{u.email}</td>
                    <td className="px-4 py-3 text-slate-400">{u.phone || '—'}</td>
                    <td className="px-4 py-3">
                      <Badge label={u.role}
                        color={u.role === 'admin' ? PRIMARY : '#475569'}
                        bg={u.role === 'admin' ? '#fdf0f4' : '#f1f5f9'} />
                    </td>
                    <td className="px-4 py-3">
                      {u.email_verified
                        ? <Badge label="Yes" color="#10b981" bg="#d1fae5" />
                        : <Badge label="No"  color="#dc2626" bg="#fee2e2" />}
                    </td>
                    <td className="px-4 py-3">
                      {u.is_blocked
                        ? <Badge label="Blocked"  color="#dc2626" bg="#fee2e2" />
                        : <Badge label="Active"   color="#10b981" bg="#d1fae5" />}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {/* Edit */}
                        <button onClick={() => setEditUser(u)} title="Edit"
                          className="w-8 h-8 rounded-lg flex items-center justify-center transition"
                          style={{ background: '#fdf0f4', color: PRIMARY }}
                          onMouseEnter={e => e.currentTarget.style.background = '#fce0e8'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fdf0f4'}>
                          <Edit2 size={14} strokeWidth={2.2} />
                        </button>
                        {/* Reset Password */}
                        <button onClick={() => setResetUser(u)} title="Reset password"
                          className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                          style={{ background: '#fef3c7', color: '#d97706' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#fde68a'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fef3c7'}>
                          <Key size={13} />
                        </button>
                        {/* Block / Unblock */}
                        {u.is_blocked ? (
                          <button onClick={() => setConfirm({ type: 'unblock', user: u })} title="Unblock"
                            className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                            style={{ background: '#d1fae5', color: '#059669' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#a7f3d0'}
                            onMouseLeave={e => e.currentTarget.style.background = '#d1fae5'}>
                            <UserCheck size={13} />
                          </button>
                        ) : (
                          <button onClick={() => setConfirm({ type: 'block', user: u })} title="Block"
                            className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                            style={{ background: '#fee2e2', color: '#dc2626' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#fecaca'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fee2e2'}>
                            <UserX size={13} />
                          </button>
                        )}
                        {/* Delete */}
                        <button onClick={() => setConfirm({ type: 'delete', user: u })} title="Delete"
                          className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                          style={{ background: '#fee2e2', color: '#dc2626' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#fecaca'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fee2e2'}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {data.users.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400 text-sm">No users found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {data.totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-50">
            <p className="text-xs text-slate-400">
              Page {data.page} of {data.totalPages} · {data.total} users
            </p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-40 transition"
                style={{ background: '#f1f5f9', color: '#475569' }}>
                <ChevronLeft size={14} />
              </button>
              <button onClick={() => setPage(p => Math.min(data.totalPages, p + 1))} disabled={page >= data.totalPages}
                className="w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-40 transition"
                style={{ background: '#f1f5f9', color: '#475569' }}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {confirm?.type === 'delete' && (
        <Confirm message={`Delete user "${confirm.user.email}"? This cannot be undone.`}
          onConfirm={() => doDelete(confirm.user)}
          onCancel={() => setConfirm(null)} />
      )}
      {confirm?.type === 'block' && (
        <Confirm message={`Block user "${confirm.user.email}"? They won't be able to log in.`}
          onConfirm={() => doBlock(confirm.user, true)}
          onCancel={() => setConfirm(null)} />
      )}
      {confirm?.type === 'unblock' && (
        <Confirm message={`Unblock user "${confirm.user.email}"?`}
          onConfirm={() => doBlock(confirm.user, false)}
          onCancel={() => setConfirm(null)} />
      )}
      {editUser && (
        <EditModal user={editUser} onSave={() => { setEditUser(null); load(); }} onClose={() => setEditUser(null)} />
      )}
      {resetUser && (
        <ResetPwModal user={resetUser} onClose={() => setResetUser(null)} />
      )}
    </div>
  );
}
